<script setup>
import { computed, ref, onMounted, onUnmounted, defineExpose } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import AppButton from '@/Components/AppButton.vue'
import BootstrapModal from '@/Components/BootstrapModal.vue'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

// Emit click so parent can handle lock modal (align with TypeformCard)
const emit = defineEmits(['click', 'share'])

const layout = computed(() => props.link?.options?.layout || 'classic')
const isFeatured = computed(() => layout.value === 'featured')
const isClassic  = computed(() => layout.value === 'classic')

// Device detection (align with TwitchCard behavior)
const isDesktop = ref(true)
let __gmResize = null
onMounted(() => {
  try { isDesktop.value = window.innerWidth >= 992 } catch (e) {}
  const handler = () => { isDesktop.value = window.innerWidth >= 992 }
  __gmResize = handler
  if (typeof window !== 'undefined') window.addEventListener('resize', handler)
})
onUnmounted(() => {
  if (typeof window !== 'undefined' && __gmResize) window.removeEventListener('resize', __gmResize)
})

const mapIcon = '/template/allmyonline/assets/icons/google-maps.svg'

// Modal handling for mobile fullscreen
const modalRef = ref(null)
const modalId = computed(() => {
  const idPart = props.link?.id ?? (props.link?.url ? props.link.url.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24) : 'default')
  return `googlemaps-modal-${idPart}`
})

// Decide how this link should be handled by parent
const customIframe = computed(() => props.link?.options?.google_maps?.display_mode === 'custom' && props.link?.options?.google_maps?.custom_iframe ? props.link.options.google_maps.custom_iframe : '')
const displayMode = computed(() => (customIframe.value ? 'embed-googlemaps' : 'link-to-googlemaps'))

function onRootClick(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
  emit('click', { link: props.link, mode: displayMode.value })
}

function openMobileFullscreen(e){
  // Route through parent lock flow first
  return onRootClick(e)
}

function openUrl(e){
  // Route through parent lock flow first
  return onRootClick(e)
}
function openInSameTab(e){
  // Route through parent lock flow first
  return onRootClick(e)
}
function openInNewTab(e){
  // Route through parent lock flow first
  return onRootClick(e)
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

// Expose API so parent can trigger inline/open behavior after unlock
function openInline(){
  // When using custom iframe (embed mode), try to open fullscreen modal if available
  if (customIframe.value) {
    const inst = modalRef.value;
    // Our BootstrapModal exposes showModal(modalId) in other cards
    if (inst && typeof inst.showModal === 'function') {
      inst.showModal(modalId.value);
    }
  }
}
function playInline(){
  // Alias to match other cards' API naming
  openInline();
}

defineExpose({ openInline, playInline })
</script>

<template>
  <!-- FEATURED LAYOUT with optional Google Maps embed -->
  <GeneralEmbedButton v-if="isFeatured"
                      class="googlemap-card"
                      :link="link"
                      :appearance="appearance"
                      :buttonStyle="buttonStyle"
                      :DoBucket="DoBucket"
                      :DoEndpoint="DoEndpoint"
                      :layout="layout"
                      @click="onRootClick">
    <!-- Header with icon, title, share -->
    <div class="d-flex align-items-center mb-2"
         style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <img :src="mapIcon" alt="Google Maps" style="width:2rem"/>
      <CardTitle :title="link.title || 'Location'" layout="classic" align="center" :max="100" class="flex-grow-1" />
      <span class="d-flex align-items-center" style="gap:10px;">
        <!-- Desktop: open in new tab icon -->
        <svg v-if="isDesktop && link?.url"
             @click.prevent.stop="openInNewTab"
             width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
             role="button" aria-label="Open in new tab">
          <path :fill="appearance.button_font_color || '#000'"
                d="M14 3a1 1 0 100 2h3.586l-6.293 6.293a1 1 0 101.414 1.414L19 6.414V10a1 1 0 102 0V4a1 1 0 00-1-1h-6z"/>
          <path :fill="appearance.button_font_color || '#000'"
                d="M5 5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4a1 1 0 10-2 0v4H5V7h4a1 1 0 000-2H5z"/>
        </svg>
        <!-- Mobile: fullscreen icon (open Bootstrap modal if possible) -->
        <svg v-else-if="!isDesktop && link?.url"
             @click.prevent.stop="openMobileFullscreen"
             width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
             role="button" aria-label="Open fullscreen">
          <path :fill="appearance.button_font_color || '#000'"
                d="M3 9a1 1 0 001-1V6a3 3 0 013-3h2a1 1 0 110 2H7a1 1 0 00-1 1v2a1 1 0 01-1 1H3zm0 6a1 1 0 011 1v2a3 3 0 003 3h2a1 1 0 100-2H7a1 1 0 01-1-1v-2a1 1 0 00-1-1H3zm18-6a1 1 0 01-1-1V6a3 3 0 00-3-3h-2a1 1 0 100 2h2a1 1 0 011 1v2a1 1 0 001 1h2zm0 6h-2a1 1 0 00-1 1v2a1 1 0 01-1 1h-2a1 1 0 100 2h2a3 3 0 003-3v-2a1 1 0 011-1z"/>
        </svg>
        <!-- Share icon -->
        <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
    </div>

    <!-- Map embed or CTA -->
    <div class="position-relative" style="max-height: 260px; overflow:hidden;" :class="appearance.buttons">
      <div v-if="customIframe" v-html="customIframe" :class="appearance.buttons"></div>
      <div v-else class="p-3 ">
        <AppButton width="100%" :secondary="true" @click.stop="onRootClick" :fontWeight="appearance.font_weight" :custom-class="appearance.buttons" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color"  ignore-plan-restriction>Open in Google Maps</AppButton>
          <div class="mt-2 d-flex align-items-center justify-content-center"  style="font-size:11px;">
          Powered by <img :src="mapIcon" alt="Google Maps" style="height:16px; margin-left:6px" />
        </div>
      </div>
    </div>

    <!-- Mobile fullscreen modal for Google Maps -->
      <teleport to="body">
          <BootstrapModal
              ref="modalRef"
              :modal-id="modalId"
              :title="link.title || 'Location'"
              modalSize="fullscreen"
          >
              <template #body>
                  <div v-if="customIframe" class="w-100" style="height: calc(100vh - 140px); position: relative; overflow:hidden;">
                      <div v-html="customIframe" class="w-100 h-100 gm-embed"></div>
                  </div>
                  <div v-else class="p-3">
                      <AppButton width="100%" :secondary="true" @click.stop="openInSameTab" :fontWeight="appearance.font_weight" :custom-class="appearance.buttons" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color"  ignore-plan-restriction>Open in Google Maps</AppButton>
                      <div class="mt-2 d-flex align-items-center justify-content-center" style="font-size:11px; color:#888">
                          Powered by <img :src="mapIcon" alt="Google Maps" style="height:16px; margin-left:6px" />
                      </div>
                  </div>
              </template>
          </BootstrapModal>
      </teleport>

  </GeneralEmbedButton>
  <!-- CLASSIC LAYOUT -->
  <GeneralButton v-else-if="isClassic"
                 :link="link"
                 :appearance="appearance"
                 :buttonStyle="buttonStyle"
                 :DoBucket="DoBucket"
                 :DoEndpoint="DoEndpoint"
                 :layout="layout"
                 @click="onRootClick">
    <template #title>
      <CardTitle :title="link.title || 'Google Maps'" layout="classic" align="center" :max="100" />
    </template>
    <template #actions>
      <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
      </svg>
    </template>
  </GeneralButton>
</template>

<style scoped>
/* Ensure embedded iframes inside the fullscreen modal fill the available space */
.gm-embed :deep(iframe){
  width: 100% !important;
  height: 100% !important;
  border: 0 !important;
  display: block;
}
</style>
